import type { CogniteClient } from '@cognite/sdk';
import type { QueryClient } from '@tanstack/react-query';

import { datasetQueryKey } from './dataset-query-key.ts';
import type { PipelineContext } from '#plugin-chat/lib/data-pipeline/step/types.ts';
import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';
import type { ProduceDatasetFn } from '#plugin-chat/lib/tool/create-tool.types.ts';
import { DatasetNotFoundError, UpstreamNotRegisteredError } from '#plugin-chat/lib/tool/errors/index.ts';

/**
 * Minimal chat-history read surface the resolver needs to translate an
 * LLM-facing `outputDataset` name into the `(toolName, args)` tuple that
 * canonically identifies the producer.
 *
 * Matches the shape returned by `useToolOutputAccessors` — the production
 * call site passes that hook's return value straight through. Kept
 * structural so the resolver has no React dependency and remains trivially
 * testable with a plain object.
 */
export interface DatasetResolverAccessors {
  /** Return the `(toolName, args)` tuple of the tool call that staged `outputDataset`, or undefined. */
  getToolCall: (outputDataset: string) => { toolName: string; args: Record<string, unknown> } | undefined;
  /** Every known `outputDataset` name in the current thread's message history. */
  getOutputKeys: () => string[];
  /**
   * Look up a producer tool's `produceDataset` by tool name. Read back from
   * the assistant-ui model context (where `createTool` attaches it on
   * registration). Returns `undefined` for non-producer or not-yet-registered
   * tools. The resolver calls this to materialise datasets lazily on the
   * first consumer lookup — independent of whether the producer's UI has
   * rendered yet.
   */
  getProducer: (toolName: string) => ProduceDatasetFn | undefined;
}

/**
 * Resolves datasets produced by earlier tool calls, keyed either by their
 * LLM-facing `outputDataset` name or by their canonical `(toolName, args)`
 * tuple. Backed by the TanStack Query cache — no stateful store, no
 * staging overlay.
 *
 * Two materialisation paths seed the cache under a single canonical key:
 *
 * - **Streaming turn** — the producer tool's `buildProducerExecute` wrapper
 *   fires `queryClient.fetchQuery` with the real `produceDataset` as the
 *   `queryFn` before the LLM-facing `execute` runs.
 * - **Lazy** — on a consumer-driven lookup (`useDataset`, a downstream
 *   cascade via `ctx.resolve.byName`) with no cache entry yet, the resolver
 *   reads `produceDataset` back from the assistant-ui model context via
 *   `accessors.getProducer(toolName)` and invokes it inline. This covers
 *   post-reload recovery and cases where a consumer evaluates before the
 *   producer's UI has rendered.
 *
 * If the tool is not registered (unknown name, gated factory, not yet
 * mounted) the resolver throws {@link UpstreamNotRegisteredError}, making
 * the invariant violation loud and diagnosable.
 */
export interface DatasetResolver {
  /** Thread id this resolver is scoped to. Exposed for cache-key callers. */
  readonly threadId: string;
  /** Resolve a dataset by its LLM-facing `outputDataset` name. */
  byName(outputDataset: string): Promise<Dataset>;
  /** Resolve a dataset by its canonical `(toolName, args)` tuple. */
  byCall(toolName: string, args: Record<string, unknown>): Promise<Dataset>;
  /** List every known `outputDataset` name — used for error suggestions. */
  knownNames(): string[];
}

/** Dependencies accepted by {@link createDatasetResolver}. */
export interface CreateDatasetResolverDeps {
  queryClient: QueryClient;
  threadId: string;
  accessors: DatasetResolverAccessors;
  /**
   * CDF SDK client threaded into the `PipelineContext` the resolver builds
   * when it invokes `produceDataset` on a lazy lookup. Required for
   * retrieval-side producers that hit CDF APIs. `undefined` outside a
   * provider — matches `PipelineContext.client`.
   */
  client: CogniteClient | undefined;
}

/**
 * Build a {@link DatasetResolver} bound to a specific thread + query client
 * + chat-history accessors. Called from `usePipelineContext` per invocation
 * and from `useDataset`; both pass the current thread's deps.
 */
export function createDatasetResolver({
  queryClient,
  threadId,
  accessors,
  client,
}: CreateDatasetResolverDeps): DatasetResolver {
  const byCall = (toolName: string, args: Record<string, unknown>): Promise<Dataset> => {
    const queryKey = datasetQueryKey(threadId, toolName, args);
    return queryClient.fetchQuery<Dataset>({
      queryKey,
      // Lazy registry-backed materialisation. The streaming-turn path in
      // `buildProducerExecute` seeds the cache via its own `fetchQuery` call
      // with the same key, so this `queryFn` is only invoked when no prior
      // execute has run — e.g. after a page reload, or when a consumer
      // (`useDataset`, a downstream cascade) evaluates before the producer.
      // We look up `produceDataset` via the accessor, which reads it back
      // from the assistant-ui model context, then invoke it with a fresh
      // `PipelineContext` built from the resolver's own deps. `resolve` is
      // set to the resolver under construction so nested cascades recurse
      // through this same path.
      queryFn: () => {
        const produceDataset = accessors.getProducer(toolName);
        if (!produceDataset) {
          const outputDataset =
            typeof args.outputDataset === 'string' && args.outputDataset.length > 0 ? args.outputDataset : toolName;
          throw new UpstreamNotRegisteredError(toolName, outputDataset);
        }
        const ctx: PipelineContext = { resolve: resolver, client, queryClient };
        return produceDataset(args, ctx);
      },
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
    });
  };

  const byName = (outputDataset: string): Promise<Dataset> => {
    const toolCall = accessors.getToolCall(outputDataset);
    if (!toolCall) {
      throw new DatasetNotFoundError(outputDataset, accessors.getOutputKeys());
    }
    return byCall(toolCall.toolName, toolCall.args);
  };

  const resolver: DatasetResolver = {
    threadId,
    byName,
    byCall,
    knownNames: () => accessors.getOutputKeys(),
  };
  return resolver;
}
